import React, { useRef } from 'react';
import './UploadOrdersPopup.css';
import { useUploadSeedsOrders } from './useUploadSeedsOrders';
import UploadOrderSettings from './UploadOrderSettings';
import { currencySymbol, uploadTotals, fmt } from '../utils/orderUnits';

// Колонки предпросмотра: количество и цена подписаны единицей и валютой окна загрузки
const previewColumns = (unit, sym, showImport) => [
  { key: 'seedsID',           label: 'Seeds ID' },
  { key: 'seedsName',         label: 'Seed Name' },
  { key: 'year',              label: 'Year' },
  { key: 'month_num',         label: 'Month' },
  { key: 'supplierName',      label: 'Supplier' },
  { key: 'statusID',          label: 'Status' },
  { key: 'qty',               label: `Qty (${unit})` },
  { key: 'price',             label: `Price/${unit} (${sym})` },
  ...(showImport ? [
    { key: 'duties',          label: 'Duties ($)' },
    { key: 'lab_testing',     label: 'Lab Testing ($)' },
  ] : []),
  { key: 'est_del_date',      label: 'Est. Del. Date' },
  { key: 'bo_follow_up_date', label: 'BO Follow-up' },
  { key: 'tracking',          label: 'Tracking' },
];

/**
 * UploadSeedsOrdersPopup
 *
 * Props:
 *   suppliers  [{ supplierID, supplierName }]  — seeds suppliers
 *   onClose
 *   onSuccess
 */
const UploadSeedsOrdersPopup = ({ suppliers = [], onClose, onSuccess }) => {
  const fileInputRef = useRef(null);

  const {
    settings, setSettings,
    parsedRows,
    parseErrors,
    orderID, setOrderID,
    fileName,
    isSubmitting,
    submitResult,
    duplicateWarning,
    dismissDuplicateWarning,
    parseFile,
    submitOrders,
    reset,
  } = useUploadSeedsOrders({ suppliers, onSuccess });

  const unit  = settings.weight_unit === 'kg' ? 'kg' : 'lb';
  const sym   = currencySymbol(settings.currency);
  const isEUR = settings.currency === 'EUR';

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) parseFile(file);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <div className="upload-orders-overlay">
      <div className="upload-orders-modal">
        <div className="upload-orders-header">
          <h2 className="upload-orders-title">
            <i className="fa-solid fa-seedling" style={{ marginRight: 8, color: '#22c55e' }}></i>
            Upload Seeds Orders
          </h2>
          <button className="upload-orders-close" onClick={handleClose}>×</button>
        </div>

        <div className="upload-orders-body">
          {/* Note: seeds delivered don't auto-stock */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            background: '#fefce8', border: '1px solid #fde68a',
            borderRadius: 7, padding: '8px 12px', fontSize: '0.82rem', color: '#92400e',
          }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ marginTop: 1, flexShrink: 0 }}></i>
            <span>
              Seeds orders marked as <strong>Delivered (D)</strong> do <strong>NOT</strong> automatically
              update inventory stock — seeds are tracked separately and must be processed manually.
            </span>
          </div>

          {/* Order ID */}
          <div className="upload-orders-field" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <label className="upload-orders-label">
              Order ID <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              className="upload-orders-input"
              type="text"
              placeholder="e.g. SEEDS-2025-001"
              value={orderID}
              onChange={e => setOrderID(e.target.value)}
              style={{ width: 240 }}
            />
          </div>

          {/* Валюта, курс, единица веса, Import order — на весь файл */}
          <UploadOrderSettings settings={settings} onChange={setSettings} showUnit />

          {/* Suppliers hint */}
          {suppliers.length > 0 && (
            <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
              <i className="fa-solid fa-truck" style={{ marginRight: 5 }}></i>
              Available seeds suppliers:&nbsp;
              <strong>{suppliers.map(s => s.supplierName).join(', ')}</strong>
            </div>
          )}
          {suppliers.length === 0 && (
            <div style={{ fontSize: '0.82rem', color: '#f59e0b' }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5 }}></i>
              No seeds suppliers configured. Add them via Manage Suppliers.
            </div>
          )}

          {/* File upload dropzone */}
          <div
            className="upload-orders-dropzone"
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
            />
            <div className="upload-orders-dropzone-text">
              <i className="fa-solid fa-folder-open" style={{ marginRight: 8 }}></i>
              Click or drag & drop a file here
            </div>
            <div className="upload-orders-dropzone-sub">Supported: CSV, XLSX, XLS</div>
            {fileName && (
              <div className="upload-orders-file-name">
                <i className="fa-solid fa-file" style={{ marginRight: 5 }}></i>
                {fileName}
              </div>
            )}
          </div>

          {/* Validation errors */}
          {parseErrors.length > 0 && (
            <div className="upload-orders-errors">
              <div className="upload-orders-error-title">
                <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5 }}></i>
                Validation errors ({parseErrors.length})
              </div>
              {parseErrors.map((err, i) => (
                <div key={i} className="upload-orders-error-item">• {err}</div>
              ))}
            </div>
          )}

          {/* Preview table */}
          {parsedRows.length > 0 && (() => {
            const totals     = uploadTotals(parsedRows, settings, true);
            const showImport = settings.is_import || totals.importUsd > 0;
            const PREVIEW_COLUMNS = previewColumns(unit, sym, showImport);
            return (
              <div>
                <div className="upload-orders-preview-title">
                  <i className="fa-solid fa-table" style={{ marginRight: 6, color: '#22c55e' }}></i>
                  Preview — {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} ready to import
                </div>
                <div className="upload-orders-table-wrapper">
                  <table className="upload-orders-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        {PREVIEW_COLUMNS.map(col => <th key={col.key}>{col.label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.map((row, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          {PREVIEW_COLUMNS.map(col => (
                            <td key={col.key}>
                              {row[col.key] !== null && row[col.key] !== undefined ? String(row[col.key]) : '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{
                  display: 'flex', gap: 10, alignItems: 'center',
                  marginTop: 8, padding: '8px 12px',
                  background: '#f8f9fa', border: '1px solid #dde1e7', borderRadius: 7,
                  fontSize: '0.84rem', color: '#5a6270',
                }}>
                  <i className="fa-solid fa-sigma" style={{ color: '#9ba3b0' }}></i>
                  <span>{parsedRows.length} rows</span>
                  <span style={{ color: '#dde1e7' }}>·</span>
                  <span>
                    <strong>{totals.qty.toLocaleString('en-US', { maximumFractionDigits: 2 })}</strong> {unit} total
                    {unit === 'kg' && <> (≈ {fmt(totals.qtyLb)} lb)</>}
                  </span>
                  <span style={{ color: '#dde1e7' }}>·</span>
                  <span style={{ fontWeight: 600, color: '#437045' }}>
                    {sym}{fmt(totals.native)} total
                    {isEUR && parseFloat(settings.fx_rate) > 0 && <> (≈ ${fmt(totals.usd)})</>}
                  </span>
                  {totals.importUsd > 0 && (
                    <>
                      <span style={{ color: '#dde1e7' }}>·</span>
                      <span>Duties + Lab Testing ${fmt(totals.importUsd)}</span>
                    </>
                  )}
                </div>
                {totals.importUsd > 0 && !settings.is_import && (
                  <div style={{ fontSize: '0.82rem', color: '#92400e', marginTop: 6 }}>
                    <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5 }}></i>
                    Duties and Lab Testing from the file will be saved, but they count towards COGS only
                    when <strong>Import order</strong> is ticked.
                  </div>
                )}
              </div>
            );
          })()}

          {/* Result */}
          {submitResult && (
            <div className={`upload-orders-result ${submitResult.success ? 'success' : 'error'}`}>
              {submitResult.success
                ? <><i className="fa-solid fa-check" style={{ marginRight: 6 }}></i>{submitResult.message}</>
                : <><i className="fa-solid fa-xmark" style={{ marginRight: 6 }}></i>{submitResult.message}</>}
            </div>
          )}
        </div>

        <div className="upload-orders-footer">
          <button className="upload-orders-btn upload-orders-btn-cancel" onClick={handleClose}>
            Close
          </button>
          <button
            className="upload-orders-btn upload-orders-btn-submit"
            onClick={() => submitOrders(false)}
            disabled={isSubmitting || parsedRows.length === 0 || parseErrors.length > 0 || !orderID.trim()}
          >
            {isSubmitting
              ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Saving...</>
              : <><i className="fa-solid fa-floppy-disk" style={{ marginRight: 6 }}></i>
                  Save {parsedRows.length > 0 ? `(${parsedRows.length})` : ''} Orders
                </>}
          </button>
        </div>
      </div>

      {/* Duplicate warning dialog */}
      {duplicateWarning && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 28, maxWidth: 520, width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ color: '#f59e0b', fontSize: '1.3rem' }}></i>
              <strong style={{ fontSize: '1rem', color: '#1e293b' }}>Duplicate rows detected</strong>
            </div>
            <p style={{ fontSize: '0.87rem', color: '#475569', marginBottom: 10 }}>
              The following rows already exist in the database:
            </p>
            <ul style={{
              margin: '0 0 14px', padding: '0 0 0 18px',
              maxHeight: 160, overflowY: 'auto',
              fontSize: '0.83rem', color: '#334155', lineHeight: 1.7,
            }}>
              {duplicateWarning.duplicates.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 18 }}>
              Do you want to add them again as separate entries (e.g. different price)?
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                style={{
                  padding: '8px 18px', borderRadius: 7, border: '1px solid #dde1e7',
                  background: '#f8f9fa', color: '#374151', fontSize: '0.9rem', cursor: 'pointer',
                }}
                onClick={dismissDuplicateWarning}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '8px 18px', borderRadius: 7, border: 'none',
                  background: '#f59e0b', color: '#fff', fontSize: '0.9rem',
                  cursor: 'pointer', fontWeight: 600,
                }}
                onClick={() => submitOrders(true)}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 5 }}></i>Saving...</>
                  : 'Yes, add anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadSeedsOrdersPopup;
